import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO, generateServiceSchema } from '../../utils/seo';

interface Transfer {
  destination: string;
  price: number;
}

const popularTransfers: Transfer[] = [
  { destination: 'Marseille', price: 44.95 },
  { destination: 'Aix En Provence', price: 52.95 },
  { destination: 'Flayosc', price: 230.95 },
  { destination: 'Cassis', price: 78.95 },
  { destination: 'Avignon', price: 146.95 },
  { destination: 'Marseille Cruise Port', price: 44.95 },
  { destination: 'St Tropez', price: 259.95 },
  { destination: 'Arles', price: 94.95 },
  { destination: 'Marseille Train Station', price: 44.95 },
  { destination: 'Toulon', price: 137.95 },
  { destination: 'La Ciotat', price: 114.95 },
  { destination: 'Cannes', price: 274.95 },
  { destination: 'Carry-Le-Rouet', price: 40.95 },
  { destination: 'Grimaud', price: 244.95 },
  { destination: 'Hyeres', price: 179.95 },
  { destination: 'Bandol', price: 129.95 },
  { destination: 'Sanary-Sur-Mer', price: 148.95 },
  { destination: 'Gordes', price: 140.95 },
  { destination: 'Nice', price: 344.95 },
  { destination: "L'isle-Sur-La-Sorgue", price: 124.95 },
];

const benefits = [
  {
    icon: 'ri-price-tag-3-line',
    title: 'Tarifs tout compris',
    description: 'Tarifs tout compris'
  },
  {
    icon: 'ri-bank-card-line',
    title: 'Options de paiement flexibles',
    description: 'Options de paiement flexibles'
  },
  {
    icon: 'ri-time-line',
    title: 'Temps d\'attente gratuit',
    description: 'Temps d\'attente gratuit'
  },
  {
    icon: 'ri-car-line',
    title: 'Sélection variée de véhicules',
    description: 'Sélection variée de véhicules'
  }
];

const travelSteps = [
  {
    icon: 'ri-calendar-check-line',
    title: 'Réservez Facilement',
    description: 'Réservation en ligne rapide avec confirmation immédiate.'
  },
  {
    icon: 'ri-user-smile-line',
    title: 'Rencontrez votre chauffeur',
    description: 'Un visage accueillant vous attend à l\'arrivée.'
  },
  {
    icon: 'ri-steering-2-line',
    title: 'Profitez du trajet',
    description: 'Transfert privé et fiable jusqu\'à votre destination.'
  }
];

export default function MarseilleAirportPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [tripType, setTripType] = useState<'round-trip' | 'one-way'>('round-trip');
  const [passengers, setPassengers] = useState(1);
  
  useSEO({
    title: 'Transfert Aéroport Marseille vers Provence | Voyage Elite Premium',
    description: 'Transfert privé depuis l\'aéroport de Marseille (MRS) vers Aix-en-Provence, Cassis, Avignon, St Tropez et toute la Provence. Service premium 24/7 avec 300 jours de soleil.',
    keywords: 'transfert Marseille, navette Marseille aéroport, VTC Marseille Provence, transport Marseille Côte d\'Azur',
    canonical: '/marseille-airport',
    ogTitle: 'Transfert Aéroport Marseille - Provence | Voyage Elite',
    ogDescription: 'Service de transfert privé premium depuis Marseille vers la Provence et la Côte d\'Azur',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/marseille-airport#webpage`,
          url: `${siteUrl}/marseille-airport`,
          name: 'Transferts depuis Marseille Aéroport (MRS)',
          description: 'Service de transfert privé premium depuis l\'aéroport de Marseille vers toute la Provence',
          inLanguage: 'fr-FR',
        },
        generateServiceSchema(),
      ],
    },
  });

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [transfersRef, transfersInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [benefitsRef, benefitsInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [stepsRef, stepsInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* HERO SECTION WITH BOOKING FORM */}
      <section ref={heroRef} className="relative min-h-[85vh] flex items-center justify-center overflow-hidden py-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/a6af84035c5fde996ea28e00d884c7c3.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        
        <div className="relative z-10 w-full max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-8"
          >
            <h1 className="font-playfair text-4xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              Marseille Aéroport<br />
              <span className="text-alpine-gold">Votre Porte vers la Provence</span>
            </h1>
            <p className="text-slate-200 font-inter text-lg lg:text-xl max-w-2xl mx-auto">
              300 jours de soleil par an — Transferts privés vers la Méditerranée
            </p>
          </motion.div>

          {/* BOOKING FORM */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl shadow-2xl p-8 lg:p-10 max-w-4xl mx-auto"
          >
            {/* Trip Type Toggle */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={() => setTripType('round-trip')}
                className={`flex-1 px-6 py-3 rounded-xl font-inter font-medium transition-all whitespace-nowrap cursor-pointer ${
                  tripType === 'round-trip'
                    ? 'bg-alpine-gold text-night-blue'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Aller-retour
              </button>
              <button
                onClick={() => setTripType('one-way')}
                className={`flex-1 px-6 py-3 rounded-xl font-inter font-medium transition-all whitespace-nowrap cursor-pointer ${
                  tripType === 'one-way'
                    ? 'bg-alpine-gold text-night-blue'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Aller simple
              </button>
            </div>

            {/* Form Fields */}
            <div className="space-y-6">
              {/* From Location */}
              <div>
                <label className="block text-slate-700 font-inter font-medium mb-2 text-sm">De</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-alpine-gold text-xl"></i>
                  </div>
                  <input
                    type="text"
                    value="Marseille Aéroport (MRS)"
                    readOnly
                    className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-sm text-night-blue bg-slate-50 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Spacer Icon */}
              <div className="flex justify-center">
                <div className="w-10 h-10 rounded-full bg-alpine-gold/10 flex items-center justify-center">
                  <i className="ri-arrow-down-line text-alpine-gold text-xl"></i>
                </div>
              </div>

              {/* To Location */}
              <div>
                <label className="block text-slate-700 font-inter font-medium mb-2 text-sm">Vers</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-alpine-gold text-xl"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Sélectionnez votre destination"
                    className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-sm text-night-blue focus:border-alpine-gold focus:outline-none transition-colors cursor-pointer"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-slate-700 font-inter font-medium mb-2 text-sm">Départ</label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                      <i className="ri-calendar-line text-alpine-gold text-xl"></i>
                    </div>
                    <input
                      type="date"
                      className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-sm text-night-blue focus:border-alpine-gold focus:outline-none transition-colors cursor-pointer"
                    />
                  </div>
                </div>
                {tripType === 'round-trip' && (
                  <div>
                    <label className="block text-slate-700 font-inter font-medium mb-2 text-sm">Retour</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                        <i className="ri-calendar-line text-alpine-gold text-xl"></i>
                      </div>
                      <input
                        type="date"
                        className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-sm text-night-blue focus:border-alpine-gold focus:outline-none transition-colors cursor-pointer"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Passengers */}
              <div>
                <label className="block text-slate-700 font-inter font-medium mb-2 text-sm">Passagers</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                    <i className="ri-user-line text-alpine-gold text-xl"></i>
                  </div>
                  <select
                    value={passengers}
                    onChange={(e) => setPassengers(Number(e.target.value))}
                    className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-sm text-night-blue focus:border-alpine-gold focus:outline-none transition-colors cursor-pointer appearance-none"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? 'passager' : 'passagers'}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl pointer-events-none"></i>
                </div>
              </div>

              {/* Submit Button */}
              <Link
                to="/reservation"
                className="w-full inline-flex items-center justify-center gap-3 px-8 py-5 bg-alpine-gold text-night-blue rounded-xl font-inter font-bold text-lg hover:bg-night-blue hover:text-white transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-line text-xl"></i>
                Voir les tarifs
              </Link>
            </div>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
            className="mt-10 flex flex-wrap justify-center gap-8 text-white"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-alpine-gold/20 flex items-center justify-center">
                <i className="ri-shield-check-line text-alpine-gold text-2xl"></i>
              </div>
              <div>
                <p className="font-inter font-bold text-sm">Des millions de personnes</p>
                <p className="font-inter text-xs text-slate-300">nous font confiance</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-alpine-gold/20 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-alpine-gold text-2xl"></i>
              </div>
              <div>
                <p className="font-inter font-bold text-sm">Service d'assistance</p>
                <p className="font-inter text-xs text-slate-300">24h/24 et 7j/7</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-alpine-gold/20 flex items-center justify-center">
                <i className="ri-lock-line text-alpine-gold text-2xl"></i>
              </div>
              <div>
                <p className="font-inter font-bold text-sm">Personnalisé</p>
                <p className="font-inter text-xs text-slate-300">et sécurisé</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* POPULAR TRANSFERS SECTION */}
      <section ref={transfersRef} className="py-24 px-6 lg:px-12 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={transfersInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Les transferts tendances vers<br />
              <span className="text-alpine-gold">Marseille Aéroport</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularTransfers.map((transfer, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={transfersInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.03 * index }}
                className="group bg-white border-2 border-slate-200 rounded-2xl p-6 hover:border-alpine-gold hover:shadow-xl transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-slate-500 font-inter text-xs mb-2">
                      <i className="ri-map-pin-line"></i>
                      <span>Marseille Aéroport</span>
                    </div>
                    <h3 className="font-inter text-night-blue font-bold text-lg mb-1 group-hover:text-alpine-gold transition-colors">
                      {transfer.destination}
                    </h3>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-alpine-gold/10 flex items-center justify-center flex-shrink-0 group-hover:bg-alpine-gold/20 transition-colors">
                    <i className="ri-sun-line text-alpine-gold text-xl"></i>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <p className="text-slate-500 font-inter text-xs mb-2">Prix à partir de</p>
                  <p className="font-playfair text-alpine-gold font-bold text-2xl mb-4">
                    EUR {transfer.price.toFixed(2)}
                  </p>
                  <Link
                    to="/reservation"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-night-blue text-white rounded-xl font-inter font-medium text-sm hover:bg-alpine-gold hover:text-night-blue transition-all whitespace-nowrap cursor-pointer"
                  >
                    Réserver
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AIRPORT INFO SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-6">
              Marseille Aéroport
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-slate-50 to-white rounded-3xl p-10 lg:p-12 shadow-xl border border-slate-200"
          >
            <p className="text-slate-700 font-inter text-base leading-relaxed mb-6">
              Marseille, la ville française sur la Méditerranée, bénéficie d'environ <strong>300 jours de soleil par an</strong> et de kilomètres de belles plages. C'est une destination parfaite pour une escapade urbaine ainsi qu'une porte d'entrée pratique vers la Provence. Visitez le Vieux-Port, le Palais Longchamp, ou savourez un croissant et un café au bord de l'eau. Pour rejoindre la ville, vous arriverez à l'aéroport de Marseille, qui se trouve à environ 25 km (16 mi).
            </p>
            <p className="text-slate-700 font-inter text-base leading-relaxed">
              La meilleure façon de rejoindre le centre-ville est de prendre un <strong>transfert aéroport</strong>. C'est un moyen de transport super pratique, avec un minimum d'attente.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PICKUP SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-10 lg:p-12 shadow-xl border border-slate-200"
          >
            <h3 className="font-playfair text-3xl font-bold text-night-blue mb-6">
              Prise en charge à l'aéroport
            </h3>
            <p className="text-slate-700 font-inter text-base leading-relaxed">
              Évitez la file d'attente des taxis grâce à une course pré-réservée à des tarifs compétitifs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE OFFER SECTION */}
      <section ref={benefitsRef} className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Ce que nous offrons
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-user-star-line',
                title: 'Chauffeurs locaux professionnels',
                description: 'Chauffeurs locaux professionnels'
              },
              {
                icon: 'ri-price-tag-line',
                title: 'Tarifs fixes et transparents',
                description: 'Tarifs fixes et transparents'
              },
              {
                icon: 'ri-flight-takeoff-line',
                title: 'Suivi des vols pour des prises en charge ponctuelles',
                description: 'Suivi des vols pour des prises en charge ponctuelles'
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Assistance 24/7 et annulations simples',
                description: 'Assistance 24/7 et annulations simples'
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={benefitsInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.1 * index }}
                className="bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 rounded-2xl p-8 text-center hover:border-alpine-gold hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 bg-alpine-gold/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-alpine-gold text-3xl`}></i>
                </div>
                <h3 className="font-inter text-night-blue font-bold text-base mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 font-inter text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Pourquoi choisir un transfert de<br />
              <span className="text-alpine-gold">Marseille Aéroport</span>
            </h2>
            <p className="text-slate-600 font-inter text-xl">
              Il y a d'innombrables avantages
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
                className="bg-white border-2 border-slate-200 rounded-2xl p-8 text-center hover:border-alpine-gold hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 bg-alpine-gold/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-alpine-gold text-3xl`}></i>
                </div>
                <h3 className="font-inter text-night-blue font-bold text-base mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 font-inter text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TRAVEL STEPS SECTION */}
      <section ref={stepsRef} className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={stepsInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Voyagez simplement
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {travelSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={stepsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 * index }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 rounded-2xl p-8 text-center hover:border-alpine-gold hover:shadow-xl transition-all">
                  <div className="w-20 h-20 bg-alpine-gold rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <i className={`${step.icon} text-white text-4xl`}></i>
                  </div>
                  <h3 className="font-inter text-night-blue font-bold text-xl mb-4">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 font-inter text-base leading-relaxed">
                    {step.description}
                  </p>
                </div>
                {index < travelSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-alpine-gold text-3xl"></i>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRIVATE TRANSFERS ADVANTAGES */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-10 lg:p-12 shadow-xl border border-slate-200"
          >
            <h2 className="font-playfair text-3xl lg:text-4xl font-bold text-night-blue mb-8">
              Avantages des transferts privés
            </h2>
            <div className="space-y-6 text-slate-700 font-inter text-base leading-relaxed">
              <p>
                Les <strong>transferts privés depuis l'aéroport</strong> offrent l'itinéraire le plus rapide et le plus pratique de Marseille Aéroport directement à votre destination. Évitez le stress des tarifs élevés des taxis ou de la navigation dans les transports en commun après votre vol. En quelques clics, assurez-vous un trajet confortable et sûr à un prix fixe et abordable.
              </p>
              <p>
                Un <strong>chauffeur professionnel</strong> vous accueillera à l'arrivée, vous aidera avec vos bagages et vous accompagnera à votre véhicule. Pendant votre trajet, profitez du Wi-Fi et de l'accès à l'assistance client 24h/24 et 7j/7 pour tous besoins ou réservations supplémentaires. Profitez d'un service porte-à-porte sans frais cachés - parfait pour gagner du temps et améliorer votre expérience de voyage.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WORLDWIDE SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-alpine-gold/10 rounded-full mb-8">
              <i className="ri-global-line text-alpine-gold text-2xl"></i>
              <span className="font-inter font-bold text-night-blue">Présent dans le monde entier</span>
            </div>
            
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-6">
              Découvrez tous les aéroports<br />
              <span className="text-alpine-gold">à disposition de Voyage Elite</span>
            </h2>
            
            <Link
              to="/transferts"
              className="inline-flex items-center gap-3 px-10 py-5 bg-night-blue text-white rounded-full font-inter font-bold text-lg hover:bg-alpine-gold hover:text-night-blue transition-all shadow-xl whitespace-nowrap cursor-pointer mt-8"
            >
              Découvrez plus
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ABOUT US SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-alpine-gold mb-8">
              <i className="ri-award-line text-white text-4xl"></i>
            </div>
            
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-6">
              Découvrez comment nous améliorons<br />
              <span className="text-alpine-gold">votre façon de voyager</span>
            </h2>
            <p className="text-slate-600 font-inter text-xl mb-10">
              Connecté à travers le monde depuis plus d'une décennie
            </p>
            
            <Link
              to="/a-propos"
              className="inline-flex items-center gap-3 px-10 py-5 bg-night-blue text-white rounded-full font-inter font-bold text-lg hover:bg-alpine-gold hover:text-night-blue transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              Lisez à propos de nous
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-night-blue via-slate-900 to-night-blue">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-white mb-6">
              Réservez votre transfert depuis<br />
              <span className="text-alpine-gold">Marseille Aéroport</span>
            </h2>
            <p className="text-slate-300 font-inter text-xl mb-10">
              Confirmation immédiate • Assistance 24/7 • Annulation simple
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Link
                to="/reservation"
                className="inline-flex items-center gap-3 px-12 py-5 bg-alpine-gold text-night-blue rounded-full font-inter font-bold text-xl hover:bg-white transition-all shadow-2xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line text-2xl"></i>
                Réserver en ligne
              </Link>
              <a
                href="tel:+33605606006"
                className="inline-flex items-center gap-3 px-12 py-5 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-full font-inter font-bold text-xl hover:bg-white hover:text-night-blue transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-2xl"></i>
                +33 6 05 60 60 06
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-slate-300">
              <div className="flex items-center gap-2">
                <i className="ri-whatsapp-line text-green-400 text-2xl"></i>
                <a href="https://wa.me/33605606006" target="_blank" rel="noopener noreferrer" className="font-inter hover:text-alpine-gold transition-colors cursor-pointer">
                  WhatsApp disponible
                </a>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-mail-line text-alpine-gold text-2xl"></i>
                <a href="mailto:contac.driver@outlook.fr" className="font-inter hover:text-alpine-gold transition-colors cursor-pointer">
                  contac.driver@outlook.fr
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
